import apiClient from "./api";
import { getApiAccessToken } from "./UserService";
import { 
    RoleListItem, 
    ContractDropdownItem, 
    RoleDetailsResponse, 
    UserRoleRequest,
    AddressSystem 
} from "../types/UserRole";

const baseUrl = '/api/UserRole';

export const getRoles = async (): Promise<RoleListItem[]> => {
    const token = await getApiAccessToken();
    const response = await apiClient.get(`${baseUrl}/roles`, {
        headers: {
            Authorization: `Bearer ${token}`
        }
    });
    return response.data as RoleListItem[];
};

export const getAllContracts = async (): Promise<ContractDropdownItem[]> => {
    const token = await getApiAccessToken();
    const response = await apiClient.get(`${baseUrl}/contracts`, {
        headers: {
            Authorization: `Bearer ${token}`
        }
    });
    return response.data as ContractDropdownItem[];
};

export const getRoleDetails = async (roleId: number, addressSystemId: number): Promise<RoleDetailsResponse> => {
    const token = await getApiAccessToken();
    const response = await apiClient.get(`${baseUrl}/role/${roleId}/addressSystem/${addressSystemId}`, {
        headers: {
            Authorization: `Bearer ${token}`
        }
    });
    return response.data as RoleDetailsResponse;
};

export const saveUserRole = async (request: UserRoleRequest): Promise<boolean> => {
    const token = await getApiAccessToken();
    const response = await apiClient.post(`${baseUrl}/save`, request, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    });
    return response.status === 200;
};

// Get address systems directly from the API
export const getAddressSystems = async (): Promise<AddressSystem[]> => {
    const token = await getApiAccessToken();
    const response = await apiClient.get(`${baseUrl}/addressSystems`, {
        headers: {
            Authorization: `Bearer ${token}`
        }
    });
    const data = response.data as AddressSystem[];
    return (data || []).map(s => ({ ...s, name: s.name?.trim() ?? '' }));
};

export const getContractsForAddressSystem = async (addressSystemId: number): Promise<ContractDropdownItem[]> => {
    const token = await getApiAccessToken();
    const response = await apiClient.get(`${baseUrl}/contracts/${addressSystemId}`, {
        headers: {
            Authorization: `Bearer ${token}`
        }
    });
    return response.data as ContractDropdownItem[];
};

/**
 * Get the current user's role permissions for a specific address system
 * This returns the role details including all functions and their access status
 */
export const getCurrentUserPermissions = async (userId: number, addressSystemId: number): Promise<RoleDetailsResponse | null> => {
    try {
        // First, get the user's role ID from their user details
        const { getUserById } = await import('./UserService');
        const user = await getUserById(userId);
        
        if (!user || !user.role || !user.role.id) {
            console.warn('getCurrentUserPermissions: User has no role assigned');
            return null;
        }
        
        const roleId = user.role.id;
        
        // Now fetch the role details with function permissions for this address system
        const roleDetails = await getRoleDetails(roleId, addressSystemId);
        
        return roleDetails;
        
    } catch (error) {
        console.error('Error fetching current user permissions:', error);
        return null;
    }
};
